import { AnimateElement } from "utils/animations";
import { useEffect, useRef, useCallback } from "react";

export type UseAnimationReturn = ReturnType<typeof useAnimation>;

export type AnimateValueProps = {
  from: number;
  to: number;
  duration: number;
  easing?:
    | "ease"
    | "linear"
    | "easeInQuad"
    | "easeOutQuad"
    | "easeInOutQuad"
    | "easeOutCubic"
    | "easeInCubic";
  onUpdate: (p: number) => void;
  onComplete?: () => void;
};

export function useAnimation() {
  const animationRef = useRef<{ cancel: () => void } | null>(null);
  const isAnimatingRef = useRef(false);
  const animationTokenRef = useRef(0);

  const cancel = () => {
    animationRef.current?.cancel();
    isAnimatingRef.current = false;
  };

  const start = useCallback((options: AnimateValueProps) => {
    cancel();

    const currentToken = ++animationTokenRef.current;
    isAnimatingRef.current = true;

    const timingFunction =
      options.easing === "linear" || options.easing === "easeInCubic"
        ? options.easing
        : "ease";

    const update = (p: number) => {
      if (currentToken !== animationTokenRef.current) return;
      options.onUpdate(p);
    };

    const onFinish = () => {
      if (currentToken !== animationTokenRef.current) return;
      options.onComplete?.();
      isAnimatingRef.current = false;
    };

    animationRef.current = {
      cancel: () => {
        animationTokenRef.current += 1;
        isAnimatingRef.current = false;
      }
    };

    AnimateElement(
      options.duration,
      [options.from, options.to],
      update,
      onFinish,
      timingFunction
    );
  }, []);

  const isAnimating = () => isAnimatingRef.current;

  useEffect(() => {
    return () => {
      cancel();
    };
  }, []);

  return { start, cancel, isAnimating };
}

function onInsertObserver(
  selector: string,
  callback: (node: Element) => void
): MutationObserver | null {
  // 1. If it already exists, triggers and returns null (no observer)
  const existing = document.querySelector(selector);
  if (existing) {
    callback(existing);
    return null;
  }

  // 2. Otherwise, observes until finding the first
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType === Node.ELEMENT_NODE && node instanceof Element) {
          if (node.matches(selector)) {
            callback(node);
            observer.disconnect(); // to observe only until finding
            return;
          }

          const match = node.querySelector(selector);
          if (match) {
            callback(match);
            observer.disconnect();
            return;
          }
        }
      }
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });

  return observer;
}

export function onElementInserted(
  selector: string,
  maxWait = 400
): Promise<Element | null> {
  return new Promise<Element | null>((res) => {
    const observer = onInsertObserver(selector, (el) => {
      res(el);
      if (observer) observer.disconnect();
    });

    if (maxWait > 0) {
      setTimeout(() => {
        res(null);
        if (observer) observer.disconnect();
      }, maxWait);
    }
  });
}
